import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

const renderFileSize = (size) => {
  if (size > 999999) {
    return Math.round(size / 1000000) + ' MB';
  } else if (size > 999) {
    return Math.round(size / 1000) + ' kB';
  } else {
    return size + ' bytes';
  }
};

const FileUploadField = ({
  input,
  disabled,
  label,
  labelRequired,
  maxUploads = 1,
  meta: { touched, error, warning, submitError },
  helpText,
  cssClass,
}) => {
  const [files, setFiles] = useState(() => (Array.isArray(input.value) && input.value.length > 0 ? [...input.value] : []));
  const [prevInputValue, setPrevInputValue] = useState(input.value);

  // When react-final-form restores / reinitialises the field value, mirror it into local state.
  // See https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
  if (input.value !== prevInputValue) {
    setPrevInputValue(input.value);
    if (Array.isArray(input.value) && input.value.length > 0) {
      setFiles(input.value);
    }
  }

  // Helper to convert a File to base64
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]); // get base64 part only
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    const newFiles = await Promise.all(
      selectedFiles.map(async (file) => ({
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        base64: await fileToBase64(file),
      })),
    );
    setFiles((prev) => (maxUploads > 1 ? [...prev, ...newFiles] : newFiles));
    // Optionally, clear the input value so the same file can be selected again
    event.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const { onChange, onBlur } = input;

  useEffect(() => {
    onChange(files);
    onBlur();
  }, [files, onChange, onBlur]);

  const { name } = input;

  let finalError = error;
  if (!finalError) {
    finalError = submitError;
  }

  return (
    <div className={`form-group file${cssClass ? ' ' + cssClass : ''}${touched && error ? ' error' : ''}`}>
      {!disabled && <input type="file" id={name} name={name} multiple={maxUploads > 1} onChange={handleFileChange} disabled={disabled} />}
      {label && (
        <label className={`btn btn-primary ${disabled ? 'disabled' : ''}`} htmlFor={name}>
          {label}
          {labelRequired && <abbr title="Required">*</abbr>}
        </label>
      )}
      {helpText && <div className="text-info">{helpText}</div>}
      {touched && finalError && <div className="text-danger">{finalError}</div>}
      {touched && warning && !finalError && <div className="text-warning">{warning}</div>}
      {files.map((file, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key
        <p key={index} className="file-info">
          <button type="button" className="btn btn-danger btn-xs pull-right" onClick={() => removeFile(index)}>
            <span className="fa fa-minus me-2"></span>Remove file
          </button>
          {file.fileName + ' - ' + renderFileSize(file.fileSize)}
        </p>
      ))}
    </div>
  );
};

FileUploadField.propTypes = {
  input: PropTypes.object.isRequired,
  label: PropTypes.string,
  maxUploads: PropTypes.number,
  meta: PropTypes.object,
  helpText: PropTypes.string,
  cssClass: PropTypes.string,
  disabled: PropTypes.bool,
  labelRequired: PropTypes.bool,
};

export default FileUploadField;
